import { createContext, useContext } from "react";

/**
 * Tryb „Widok podwykonawcy": wewnętrzny użytkownik GK ogląda zamówienie tak,
 * jak widzi je przypisany podwykonawca (modal na stronie zamówienia).
 *
 * Komponenty współdzielone przez widok GK i widok podwykonawcy sprawdzają ten
 * kontekst obok `user.role === "contractor"`: w podglądzie renderują wariant
 * podwykonawcy, ale wszystkie akcje (formularze, mutacje) są wyłączone.
 */
interface ContractorPreviewState {
  isPreview: boolean;
  /** Czy podglądany wariant pokazuje stawki (wg grantów kont podwykonawcy lub przełącznika). */
  canSeeRates: boolean;
  /** Wymuszenie wariantu stawek z przełącznika w modalu; undefined = wg grantów kont. */
  ratesOverride?: "visible" | "hidden";
  /**
   * Tryb „Realizacja": pracownik GK z nadanym dostępem tego rodzaju ogląda zamówienie
   * na stronie podwykonawcy (OrderDetailPage przełącza się na ContractorOrderDetailPage).
   * Dane idą z żywego /orders/{id}/ (backend maskuje kwoty), akcje wykonawcze (pliki
   * pozycji, raporty tygodniowe, komentarze) działają; wnioski o zmianę, wnioski o POCK,
   * koszty zewnętrzne i przyjęcie zlecenia są schowane, a panele DLC_01 tylko do odczytu.
   */
  isExecution?: boolean;
}

const ContractorPreviewContext = createContext<ContractorPreviewState>({
  isPreview: false,
  canSeeRates: true,
  isExecution: false,
});

export const ContractorPreviewProvider = ContractorPreviewContext.Provider;

export function useContractorPreview() {
  return useContext(ContractorPreviewContext);
}

/**
 * Flagi etapu projektowego (Projekt techniczny / Kosztorys) liczone są dla PYTAJĄCEGO —
 * użytkownik GK dostaje can_manage (i kuchnię zakupową `proceeding`), których podwykonawca
 * nigdy nie widzi. W podglądzie przeliczamy je tak, jak wychodzą dla wykonawcy zamówienia:
 * pisze ten, czyje zamówienie jest linkiem PRIMARY etapu; zarządzanie i postępowanie — nie.
 * Lista pól „dla pytającego" jest spisana w backend/apps/orders/tests/test_contractor_view_parity.py.
 */
export function previewDesignStage<
  T extends {
    can_write: boolean;
    can_manage: boolean;
    proceeding: unknown;
    order_links: { kind: string; order: { id: string } | null }[];
  },
>(stage: T, orderId: string): T {
  return {
    ...stage,
    can_write: stage.order_links.some((l) => l.kind === "primary" && l.order?.id === orderId),
    can_manage: false,
    proceeding: null,
  };
}
